import type { MapDocument } from "../shared/types";
import {
  mapStandHeight,
  projectMapDepth,
  projectMapPoint,
  type VenueMapCamera,
} from "./venueMapProjection";

export type MapFocusPoint = { x: number; y: number; z?: number };
export type MapFocusTarget = {
  anchor: MapFocusPoint;
  at: { x: number; y: number };
};

/** Frame a chosen roof with enough surrounding floor to recognise its neighbours. */
export function focusVenueMapLocation(
  document: MapDocument,
  locationId: string,
  baseline: VenueMapCamera,
  width: number,
  height: number,
): (MapFocusTarget & { zoom: number }) | null {
  const location = document.locations.find((item) => item.id === locationId);
  const { geometry } = document;
  const stand = geometry.stands.find((item) => item.id === locationId);
  if (
    !location ||
    location.hidden ||
    stand?.hidden ||
    width <= 0 ||
    height <= 0 ||
    baseline.imageWidth <= 0 ||
    !Number.isFinite(width + height + baseline.imageWidth)
  )
    return null;

  const entrance = geometry.entrances.find((item) => item.id === locationId);
  const vertices = stand?.polygons.flat() || [];
  const elevation = stand ? mapStandHeight(stand) : 0;
  const anchor = vertices.length
    ? {
        x:
          (Math.min(...vertices.map(([x]) => x)) +
            Math.max(...vertices.map(([x]) => x))) /
          (2 * geometry.width),
        y:
          (Math.min(...vertices.map(([, y]) => y)) +
            Math.max(...vertices.map(([, y]) => y))) /
          (2 * geometry.height),
        z: elevation,
      }
    : {
        x: entrance ? entrance.x / geometry.width : location.x,
        y: entrance ? entrance.y / geometry.height : location.y,
        z: 0,
      };
  // The selected-location card occupies the bottom of the viewport. Keep the
  // roof above it, with space above for discovery and the mobile controls.
  const top = Math.min(64, height * 0.18);
  const bottom = Math.min(144, height * 0.6);
  const availableHeight = height - top - bottom;
  const at = { x: width / 2, y: top + availableHeight / 2 };
  if (!vertices.length) return { anchor, at, zoom: 3 };

  const camera: VenueMapCamera = {
    ...baseline,
    stageWidth: 0,
    stageHeight: 0,
    offsetX: 0,
    offsetY: 0,
    panX: 0,
    panY: 0,
  };
  const projectedAnchor = projectMapPoint(camera, anchor);
  const perspective = camera.perspective || camera.pitch !== 0;
  const anchorDepth = perspective ? projectMapDepth(camera, anchor) : 1;
  const points = vertices.flatMap(([x, y]) =>
    [0, elevation].map((z) => ({
      x: x / geometry.width,
      y: y / geometry.height,
      z,
    })),
  );
  const halfWidth = width * 0.34;
  const halfHeight = availableHeight * 0.36;
  const targetOffset = { x: at.x - width / 2, y: at.y - height / 2 };
  const fits = (zoom: number) =>
    points.every((point) => {
      const projected = projectMapPoint(camera, point);
      const ratio = perspective
        ? projectMapDepth(camera, point) / anchorDepth
        : 1;
      // Pan precedes perspective division. Recentering the elevated anchor
      // changes every corner according to its own depth, not a uniform offset.
      const x =
        (projected.x - projectedAnchor.x * ratio) * zoom +
        targetOffset.x * (ratio - 1);
      const y =
        (projected.y - projectedAnchor.y * ratio) * zoom +
        targetOffset.y * (ratio - 1);
      return Math.abs(x) <= halfWidth && Math.abs(y) <= halfHeight;
    });
  let low = 1;
  let high = 4;
  for (let attempt = 0; attempt < 32; attempt++) {
    const middle = (low + high) / 2;
    if (fits(middle)) low = middle;
    else high = middle;
  }
  return { anchor, at, zoom: fits(4) ? 4 : low };
}
